import { InfoOutlined as InfoOutlinedIcon } from "@material-ui/icons";
import moment from "moment";
import React from "react";
import styled, { css } from "styled-components";

import { WorkspacePlanTier } from "common/interfaces";
import { getTieredSeatBreakdown, TieredSeatBreakdown } from "common/utils/stripeUtils";
import { Divider } from "js/Components/Divider";
import { FlexLayout } from "js/Components/FlexLayout";
import { TextContent } from "js/Components/TextContent";
import CheckoutController, { CheckoutControllerState } from "js/controllers/CheckoutController";
import { t } from "js/i18n";
import { themeColors } from "js/react/sharedStyles";
import { PromoForm } from "js/react/views/Billing/Checkout/components/PromoForm";
import { SeatsSelect } from "js/react/views/Billing/Checkout/components/SeatsSelect";
import { formatPrice } from "js/react/views/Billing/utils/prices";

const SummaryCard = styled.div`
    background: #f7fafc;
    border: 1px solid #dae9f0;
    border-radius: 2px;
    padding: 20px 10px;
    width: 100%;
    max-width: 300px;
    display: flex;
    flex-direction: column;
    gap: 20px;
`;

const SummaryTitle = styled.div`
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 600;
    font-size: 10px;
    line-height: normal;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    color: #444444;
    min-width: 100%;
    width: min-content;
`;

const SummaryContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 15px;
    width: 100%;
`;

const SummaryToggle = styled.button`
    background: none;
    border: none;
    color: #666;
    font-family: "Source Sans Pro";
    font-size: 10px;
    font-weight: 600;
    font-style: normal;
    line-height: normal;
    letter-spacing: 0.5px;
    text-transform: uppercase;
    cursor: pointer;
    padding: 0;
    display: flex;
    align-items: center;
    gap: 5px;
`;

const DueTodayText = styled.div`
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 600;
    font-size: 14px;
    line-height: 1.2;
    letter-spacing: 0.5px;
    color: #444444;
`;

const DueDateText = styled.div`
    font-family: "Source Sans Pro", sans-serif;
    font-weight: 400;
    font-size: 12px;
    line-height: 1.25;
    color: #888888;
`;

const SeatsContainer = styled.div<{ $useMobileLayout?: boolean }>`
    display: flex;
    flex-direction: column;
    gap: 6px;
    overflow: visible;
    ${props => props.$useMobileLayout && css`
        flex-direction: row;
        justify-content: space-between;
    `}
`;

const SeatsSection = styled.div`
    display: flex;
    flex-direction: column;
    gap: 10px;
    width: 100%;
    overflow: visible;
`;

const SeatPricingNote = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 8px;
    padding: 10px;
    background: ${themeColors.rollover};
    border: 1px solid ${themeColors.lightBlue};
    border-radius: 8px;
`;

const SeatPricingNoteIcon = styled(InfoOutlinedIcon)`
    &&& {
        font-size: 16px;
        color: ${themeColors.ui_blue};
    }
`;

const DownArrowIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
        <g clipPath="url(#clip0_68_229)">
            <path d="M4.63125 5.36865L7.5 8.23115L10.3688 5.36865L11.25 6.2499L7.5 9.9999L3.75 6.2499L4.63125 5.36865Z" fill="#11A9E2" />
        </g>
        <defs>
            <clipPath id="clip0_68_229">
                <rect width="15" height="15" fill="white" />
            </clipPath>
        </defs>
    </svg>
);

const UpArrowIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 15 15" fill="none">
        <path d="M10.3688 9.63135L7.5 6.76885L4.63125 9.63135L3.75 8.7501L7.5 5.0001L11.25 8.7501L10.3688 9.63135Z" fill="#11A9E2" />
    </svg>
);

function SummaryAmountRow({
    label,
    amount,
    testId
}: {
    label: string;
    amount: string;
    testId?: string;
}) {
    return (
        <FlexLayout justify="space-between">
            <TextContent helper>{label}</TextContent>
            <TextContent helper>
                {testId ? <span data-testid={testId}>{amount}</span> : amount}
            </TextContent>
        </FlexLayout>
    );
}

function renderSummaryLineItems({
    breakdown,
    productDisplayName,
    isAnnual,
    total,
    currency,
    conversionRate,
    prorationCredit,
    dueNowTax
}: {
    breakdown: TieredSeatBreakdown | null;
    productDisplayName: string;
    isAnnual: boolean;
    total: number;
    currency: string;
    conversionRate: number;
    prorationCredit: number;
    dueNowTax: number;
}) {
    const taxRow = (
        <SummaryAmountRow
            label={t("Tax")}
            amount={dueNowTax > 0 ? t("+ {{amount}}", { amount: formatPrice(dueNowTax, currency) }) : t("-")}
        />
    );
    const prorationRow = prorationCredit > 0 && (
        <SummaryAmountRow
            label={t("Proration credit")}
            amount={formatPrice(-prorationCredit, currency)}
        />
    );

    if (breakdown) {
        return (
            <>
                <SummaryAmountRow
                    label={t("{{productDisplayName}} plan ({{count}} seats included)", {
                        productDisplayName,
                        count: breakdown.includedSeatCount
                    })}
                    amount={formatPrice(breakdown.includedAmountCents / 100 * conversionRate, currency)}
                />
                {breakdown.additionalSeatCount > 0 && (
                    <SummaryAmountRow
                        label={t(
                            breakdown.additionalSeatCount === 1
                                ? "{{count}} additional seat"
                                : "{{count}} additional seats",
                            { count: breakdown.additionalSeatCount }
                        )}
                        amount={formatPrice(breakdown.additionalAmountCents / 100 * conversionRate, currency)}
                    />
                )}
                <SummaryAmountRow
                    label={t("Subtotal")}
                    amount={formatPrice(total, currency)}
                    testId="plan-summary-subtotal"
                />
                {prorationRow}
                {taxRow}
            </>
        );
    }

    return (
        <>
            <SummaryAmountRow
                label={isAnnual
                    ? t("{{price}} x {{count}} months", { price: formatPrice(total / 12, currency), count: 12 })
                    : t("{{price}} x {{count}} month", { price: formatPrice(total, currency), count: 1 })}
                amount={formatPrice(total, currency)}
                testId="plan-summary-subtotal"
            />
            {prorationRow}
            {taxRow}
        </>
    );
}

export const PlanSummary = CheckoutController.withState(
    function PlanSummary({
        expandable,
        tier,
        quantity = 1,
        priceId
    }: { expandable?: boolean, tier: WorkspacePlanTier } & CheckoutControllerState) {
        const [expanded, setExpanded] = React.useState(false);

        const isTrial = CheckoutController.availableTrialDays > 0;
        const billingInterval = CheckoutController.billingInterval;
        const productDisplayName = CheckoutController.productDisplayName;
        const isAnnual = billingInterval === "year";
        const checkoutType = CheckoutController.checkoutType;

        const {
            dueNowTax,
            dueNextTax,
            dueNext,
            dueNow,
            total,
            prorationCredit
        } = CheckoutController.amounts;

        const trialExpirationDate = moment().add(CheckoutController.availableTrialDays, "days").format("MMMM Do, YYYY");
        const nextBillingDate = isTrial ? trialExpirationDate : isAnnual
            ? moment().add(1, "year").format("MMMM Do, YYYY")
            : moment().add(1, "month").format("MMMM Do, YYYY");

        const dueNowTaxString = dueNowTax > 0 ? t(" plus tax") : "";
        const dueNextTaxString = dueNextTax > 0 ? t(" plus tax") : "";

        const showSeatsSelect = tier === WorkspacePlanTier.TEAM;

        const currency = CheckoutController.currency;
        const conversionRate = CheckoutController.conversionRate ?? 1;
        const breakdown = getTieredSeatBreakdown(priceId, quantity);
        const headerAmount = breakdown
            ? (breakdown.includedAmountCents / 100) * conversionRate
            : total;

        const lineItems = renderSummaryLineItems({
            breakdown,
            productDisplayName,
            isAnnual,
            total,
            currency,
            conversionRate,
            prorationCredit,
            dueNowTax
        });

        if (expandable) {
            return (
                <FlexLayout direction="column" gap={10} width="fill" style={{ overflowY: "visible", overflowX: "visible" }}>
                    {showSeatsSelect && (<>
                        <SeatsContainer $useMobileLayout>
                            <TextContent center>{t("Seats")}</TextContent>
                            <SeatsSelect disabled={checkoutType === "reactivate"} />
                        </SeatsContainer>
                        <Divider margin={15} />
                    </>)}
                    {expanded &&
                        <FlexLayout direction="column" gap={5} padding="0 10px">
                            <FlexLayout justify="space-between">
                                <TextContent helper>{t("{{productDisplayName}} plan - {{interval}} billing", { productDisplayName, interval: t(isAnnual ? "Annual" : "Monthly") })}</TextContent>
                                <TextContent helper>{formatPrice(headerAmount, currency)}/{t(billingInterval)}</TextContent>
                            </FlexLayout>
                            {lineItems}
                            <Divider margin={10} />
                            <FlexLayout justify="space-between">
                                <DueTodayText>{t("Due today: {{amount}}", { amount: formatPrice(dueNow, currency) })}{dueNow > 0 ? dueNowTaxString : ""}</DueTodayText>
                                <SummaryToggle onClick={() => setExpanded(false)}>
                                    {t("HIDE SUMMARY")}
                                    <UpArrowIcon />
                                </SummaryToggle>
                            </FlexLayout>
                            <FlexLayout justify="space-between" align="center">
                                <DueDateText>{t("Due {{date}}: {{amount}}", { date: nextBillingDate, amount: formatPrice(dueNext, currency) })}{dueNextTaxString}</DueDateText>
                            </FlexLayout>
                        </FlexLayout>
                    }
                    {!expanded &&
                        <FlexLayout direction="column" gap={5} padding="0 10px">
                            <FlexLayout justify="space-between" align="center">
                                <DueTodayText>{t("Due today: {{amount}}", { amount: formatPrice(dueNow, currency) })}{dueNow > 0 ? dueNowTaxString : ""}</DueTodayText>
                                <SummaryToggle onClick={() => setExpanded(true)}>
                                    {t("VIEW SUMMARY")}
                                    <DownArrowIcon />
                                </SummaryToggle>
                            </FlexLayout>
                            <FlexLayout justify="space-between" align="center">
                                <DueDateText>{t("Due {{date}}: {{amount}}", { date: nextBillingDate, amount: formatPrice(dueNext, currency) })}{dueNextTaxString}</DueDateText>
                            </FlexLayout>
                        </FlexLayout>
                    }
                </FlexLayout>
            );
        }

        return (
            <SummaryCard>
                <SummaryTitle>{t("Summary")}</SummaryTitle>
                <SummaryContent>
                    <FlexLayout width="fill" direction="column" gap={5}>
                        <FlexLayout justify="space-between">
                            <TextContent helper>{t("{{productDisplayName}} plan - {{interval}} billing", { productDisplayName, interval: t(isAnnual ? "Annual" : "Monthly") })}</TextContent>
                            <TextContent helper>{formatPrice(headerAmount, currency)} / {t(billingInterval)}</TextContent>
                        </FlexLayout>
                    </FlexLayout>
                    <Divider />
                    {showSeatsSelect && (<>
                        <SeatsSection>
                            <SeatsContainer>
                                <TextContent>
                                    {breakdown
                                        ? t("Seats ({{count}} seats included)", { count: breakdown.includedSeatCount })
                                        : t("Seats")}
                                </TextContent>
                                <SeatsSelect disabled={checkoutType === "reactivate"}
                                />
                            </SeatsContainer>
                            {breakdown && (
                                <SeatPricingNote data-testid="plan-summary-seat-pricing-note">
                                    <SeatPricingNoteIcon />
                                    <TextContent note>
                                        {t("Your plan includes {{count}} seats. Additional seats are {{price}}/{{interval}} per user.", {
                                            count: breakdown.includedSeatCount,
                                            price: formatPrice((breakdown.additionalUnitAmountCents / 100) * conversionRate, currency),
                                            interval: t(billingInterval)
                                        })}
                                    </TextContent>
                                </SeatPricingNote>
                            )}
                        </SeatsSection>
                        <Divider />
                    </>)}
                    <FlexLayout width="fill" direction="column" gap={5}>
                        {lineItems}
                    </FlexLayout>
                    <Divider />
                    <FlexLayout width="fill" direction="column" gap={5}>
                        <FlexLayout justify="space-between">
                            <TextContent sectionLabel>{t("Due Today")}</TextContent>
                            <TextContent sectionLabel>{formatPrice(dueNow, currency)}{dueNow > 0 ? dueNowTaxString : ""}</TextContent>
                        </FlexLayout>
                        <FlexLayout justify="space-between">
                            <TextContent helper>{t("Due {{date}}", { date: nextBillingDate })}</TextContent>
                            <TextContent helper>{formatPrice(dueNext, currency)}{dueNextTaxString}</TextContent>
                        </FlexLayout>
                    </FlexLayout>
                    <FlexLayout width="fill" justify="center">
                        <PromoForm />
                    </FlexLayout>
                </SummaryContent>
            </SummaryCard>
        );
    }
);
